import {
    ActivityChannelEnum,
    BookingDeposit,
    BookingStaffLabelEnum,
    BookingStatusEnum,
    BookingTypeEnum,
    CustomerBlockInfo,
    GoogleBookingInfo,
    OpenTime,
    PreAuthStatusEnum,
    Weekday
} from '@/__generated__/types';
import { Terms } from './google/feeds/merchant';

export enum BookingType {
    AutoAccept = 'Auto Accept',
    ManuallyAccept = 'Manually Accept'
}

/**
 * How available times decide whether the free tables can seat a party.
 * Unset behaves as CombinedTables.
 */
export enum TableAvailabilityPolicy {
    CombinedTables = 'Combined Tables',
    SingleTable = 'Single Table'
}

export enum DepositChargeType {
    perTable = 'Per Table',
    perPerson = 'Per Person'
}

export enum NotificationTriggerType {
    onCreate = 'onCreate',
    onUpdate = 'onUpdate',
    onCancel = 'onCancel'
}

export enum BookingDepositStatus {
    NoDeposit = 'No Deposit',
    Authorized = 'Authorized',
    Captured = 'Captured',
    Cancelled = 'Cancelled'
}

export enum BookingDepositAction {
    Capture = 'capture',
    Cancel = 'cancel'
}

export type AutoConfirmSettings = {
    bookingType?: BookingType;
    maxPerBooking?: number;
    autoAcceptSettings?: AutoAcceptSettings;
};

export type AutoAcceptSettings = {
    autoAcceptMax: number;
};

export type NotificationSendOnStatus = 'none' | 'confirmed' | 'cancelled';

export type NotificationSendOnBookingChanged = 'bookingTime' | 'partySize';

export type ActivityChannel = 'portal' | 'staff_portal' | 'APP' | 'POS' | 'Google' | 'deposit_webhook' | 'ai_agent';

export type NotificationRule = {
    enabled: boolean;
    excludeChannels?: ActivityChannel[];
};

export type NotificationSendOn = {
    statusEntered?: Partial<Record<NotificationSendOnStatus, NotificationRule>>;
    bookingChanged?: Partial<Record<NotificationSendOnBookingChanged, NotificationRule>>;
};

export type SMSConfig = {
    isEnabled?: boolean;
    sendOn?: NotificationSendOn;
};

export type EmailTriggers = Partial<Record<NotificationTriggerType, boolean>>;

export type NoticeConfig = {
    isDefault?: boolean;
    content?: string;
};

export type EmailConfig = {
    merchantNotificationTriggers?: EmailTriggers;
    merchantNotificationEmails?: string[];
    title?: NoticeConfig;
    description?: NoticeConfig;
    sendOn?: NotificationSendOn;
};

export type OnlineBookingNotice = {
    pendingBookingNotice?: {
        content?: string;
        isDefault?: boolean;
    };
    maxNoNotice?: {
        content?: string;
        isDefault?: boolean;
    };
    tableRequestNotice?: {
        content?: string;
        isDefault?: boolean;
    };
    depositNotice?: {
        content?: string;
        isDefault?: boolean;
    };
    bookingNoticePopup?: {
        content?: string;
        isDefault?: boolean;
    };
};

export type Notification = {
    sms?: SMSConfig;
    email?: EmailConfig;
    onlineBookingNotice?: OnlineBookingNotice;
    terms?: Terms;
};

/**
 * A window of absolute time a pre-auth rule can match against.
 *
 * Both bounds are Unix timestamps in milliseconds. The interval is half-open:
 * `startTime <= bookingTime < endTime`. Merchant-local dates are converted to absolute
 * timestamps before saving, so the backend matcher never parses dates or resolves a zone.
 */
export type PreAuthBookingTimeRange = {
    startTime: number;
    endTime: number;
};

/**
 * A conditional pre-auth rule. Rules are evaluated in order and the first match wins.
 * A rule matches when every condition it declares is satisfied.
 */
export type PreAuthRule = {
    /** Merchant-facing label, not shown to customers. */
    name?: string;

    /** Matches when the booking carries every one of these tags. */
    bookingTags?: string[];

    /** Matches when the booking start time falls inside any one of these windows. */
    bookingTimeRanges?: PreAuthBookingTimeRange[];

    /** Matches when the party size is greater than or equal to this number. */
    minPartySize?: number;

    /** The pre-authorization amount in cents (e.g., 10000 represents $100.00). */
    priceInCents: number;

    chargeType: DepositChargeType;
};

export type depositConfig = {
    /**
     * Whether a deposit is required for booking.
     */
    enabled?: boolean;

    isSurchargeOnMerchant?: boolean;

    /**
     * @deprecated Use `rules` instead. The pre-authorization amount in cents
     * (e.g., 500 represents $5.00). Retained for merchants not yet migrated to rules.
     */
    priceInCents?: number;

    /** @deprecated Use `rules` instead. Retained for merchants not yet migrated to rules. */
    chargeType?: DepositChargeType;

    /**
     * @deprecated Use `rules` with `minPartySize` instead. Retained for merchants not yet
     * migrated to rules.
     */
    minPartySizeRequiringDeposit?: number;

    /**
     * Ordered conditional rules. Presence, not length, is what switches modes: when the array is
     * present it fully replaces the deprecated flat
     * priceInCents/chargeType/minPartySizeRequiringDeposit behaviour, so the first matching rule
     * decides the pre-authorization and no match — including an empty array — means none is
     * taken. Only an absent array falls back to the flat config.
     */
    rules?: PreAuthRule[];
};

export type CloseDate = {
    guestLimit?: number;
    start?: number;
    end?: number;
    date?: string;
};

export type BookingSettingsRequirements = {
    seating?: {
        enableHighChairs?: boolean;
    };
};

export type BookingRequirements = {
    seating?: {
        highChairs?: number;
    };
};

/**
 * Configuration for booking settings.
 */
/**
 * Configuration settings for managing booking sessions and their constraints.
 *
 * This type defines all the necessary parameters to control booking behavior,
 * including timing constraints, capacity limits, deposit requirements, and
 * operational hours.
 *
 * @example
 * ```typescript
 * const settings: BookingSettings = {
 *   bookingIntervals: 30,
 *   sessionDuration: 60,
 *   bookingTimeConstraint: 30,
 *   bookingModificationConstraint: 30,
 *   sessionMaxCapacity: 10,
 *   gracePeriod: 15,
 *   deposit: { amount: 25.00, currency: 'USD' }
 * };
 * ```
 */
export type BookingSettings = {
    isBookingClosed?: boolean;
    isBookingDisabled?: boolean;

    /**
     * The interval (in minutes) between each booking session.
     */
    bookingIntervals: number;

    /**
     * The duration (in minutes) of each booking session.
     */
    sessionDuration: number;

    /**
     * The time constraint (in minutes) to lock a booking in advance.
     * For example, if a booking is set for 7:00, updates (e.g., changing the
     * number of people or canceling the booking) are not allowed after 6:30.
     */
    bookingTimeConstraint: number;

    /**
     * The time constraint (in minutes) to modify (cancel or update) a booking in advance.
     * For example, if a booking is set for 7:00, modifications (e.g., changing the
     * number of people or canceling the booking) are not allowed after 6:30.
     * in minutes, formatted as a number
     */
    bookingModificationConstraint?: number;

    gracePeriod?: number;

    sessionMaxCapacity: number;

    minPartySizePerBooking?: number;

    /**
     * The maximum number of bookings allowed for each available time slot.
     * When null, no per-slot booking limit is applied.
     */
    maxBookingsPerSlot: number | null;

    /**
     * The maximum number of assigned tables allowed for existing bookings in each available time slot.
     * When null, no per-slot assigned table limit is applied.
     */
    maxAssignedTablesPerSlot: number | null;

    /**
     * How available times check table capacity for a party. When unset, the API behaves as
     * CombinedTables (the free tables' seats are added together).
     */
    tableAvailabilityPolicy?: TableAvailabilityPolicy;

    /**
     * Deposit settings for bookings.
     */
    deposit?: depositConfig;
    autoConfirmSettings?: AutoConfirmSettings;
    notification?: Notification;
    bookingTags?: string[];
    closedTime?: {
        closeDates?: CloseDate[];
        closedHours?: {
            closedStart?: number;
            closedEnd?: number;
            weekDays?: Weekday;
        }[];
    };
    // Enable AI assistance for booking processes
    enableAIBookingAssistant?: boolean;
    requirements?: BookingSettingsRequirements;
    openTime?: OpenTime | null;
    bookingLink?: string;
};
export interface IDepositWebhookReqBody {
    dateStr?: string;
    status?: string;
    userId?: string;
    merchantId?: string;
    reference?: string; // my custom reference
    isPreAuth?: boolean;
    paymentType?: string; // PaymentType;
    isSuccess: boolean;
    payment?: {
        amount: number;
        currency?: string;
    };
}
export interface IDepositOperationRecord {
    amount?: {
        currency: string;
        value: number;
    };
    merchantAccount?: string;
    paymentPspReference?: string;
    reference?: string;
    status?: string;
}
// export const BookingUpdateLogSchema = Type.Object({
//     channel: Type.Optional(ActivityChannelSchema),
//     updatedFields: Type.Optional(
//         Type.Partial(BaseBookingData, {
//             description: "Record of updated fields from BookingData with their new values",
//             examples: [{ customerName: "Jane", bookingTime: 1704067200000, status: "confirmed" }],
//         })
//     ),
//     createdAt: Type.Optional(Type.Integer({ description: "Unix timestamp (in milliseconds) of the update" })),
//     operator: Type.Optional(OperatorSchema),
//     payload: Type.Optional(BookingUpdateLogPayloadSchema),
// });

export interface IOperator {
    channel?: ActivityChannelEnum;
    name?: string; // Name of the operator
    email?: string; // Email of the operator
    uid?: string; // Unique identifier for the operator
    userAgent?: string; // User agent of the operator
}

export interface IBookingUpdateLog {
    channel?: ActivityChannelEnum;
    updatedFields?: {
        createdAt?: number;
        updatedAt?: number;
        [key: string]: number | unknown | undefined;
    };
    createdAt?: number; // Unix timestamp (in milliseconds) of the update
    operator?: IOperator; // OperatorSchema
    payload?: unknown; // BookingUpdateLogPayloadSchema
}

export interface IBooking {
    bookingNumber: string;
    referenceNumber?: string;
    bookingTime: number;
    createdAt: number;
    updatedAt: number;
    deletedAt?: number;

    customerName?: string;
    customerPhone?: string;
    customerEmail?: string;

    note?: string;
    internalNote?: string;
    mark?: string;

    minSpend: number;
    numberOfGuests: number; // same as numberOfAdults + numberOfChildren
    numberOfAdults: number;
    numberOfChildren?: number;

    assignedTables: Record<string, string[]>;
    source: ActivityChannelEnum;
    status: BookingStatusEnum;
    preAuthStatus?: PreAuthStatusEnum;
    type?: BookingTypeEnum;

    merchantName: string;
    merchantAltName: string;
    merchantId: string;
    yearStr: string;

    deposit?: BookingDeposit;
    customerBlockInfo?: CustomerBlockInfo;
    depositActivities?: IDepositWebhookReqBody[];
    capturedActivities?: IDepositOperationRecord[];
    cancelledActivities?: IDepositOperationRecord[];
    bookingTags?: string[];
    requirements?: BookingRequirements;
    staffLabel?: BookingStaffLabelEnum;
    updateLogs?: IBookingUpdateLog[];
    google?: GoogleBookingInfo;
}

export enum BookingSource {
    Unknown = 'unknown',
    Web = 'web',
    Mobile = 'mobile',
    Other = 'other'
}

export type BookingData = {
    id: string | number;
    name: string;
    table: number | string;
    description: string;
    bookingNumber: string;
    createTime: string;
    total: number | string;
    guest: string;
    bookingType: string;
    numberPeople: number;
    promotion: string;
    qrcode: string;
};

export type BookingDetail = {
    bookingTime?: number;
    numberOfGuests?: number;
    specialInstructions: string;
    promotionId: number | string;
};

export type Geolocation = {
    coordinate?: number;
    latitude?: number;
};

export type Customer = {
    name: string;
    email?: string;
    phoneNumber?: string;
    guestNum?: string;
    address?: string;
    postcode?: string;
    foodAllergies: string;
    geolocation?: Geolocation;
};

export type Booking = {
    id: string;
    createdAt: number;
    merchantId: string;
    customer?: Customer;
    bookingDetails?: BookingDetail;
};

// export enum BookingState {
//     Pending = 'pending',
//     Confirmed = 'confirmed',
//     Cancelled = 'cancelled',
//     Rejected = 'rejected',
//     Unknown = 'unknown'
// }

export interface availableBookingTime {
    timestamp: number;
    isEnabled: boolean;
    availableBookingTags?: string[];
}

export interface FilterCriteria {
    includes?: BookingStatusEnum[];
    excludes?: BookingStatusEnum[];
    startTimestamp?: number;
    endTimestamp?: number;
}

// Define status actions that can be performed on each booking status.
// export const bookingStateAvailableActionsMap: Record<BookingState, BookingState[]> = {
//     [BookingState.Pending]: [BookingState.Confirmed, BookingState.Rejected],
//     [BookingState.Confirmed]: [BookingState.Rejected],
//     [BookingState.Cancelled]: [],
//     [BookingState.Rejected]: [],
//     [BookingState.Unknown]: []
// };

// export const getBookingState = (booking: IBooking): BookingState => {
//     // pending
//     const status = booking.status;
//     const isConfirmed = isBookingStatusSet(status, BookingStatus.IsConfirmed);
//     const isCancelled = isBookingStatusSet(status, BookingStatus.IsCancelled);
//     if (!isConfirmed && !isCancelled) return BookingState.Pending;
//     if (isCancelled) {
//         if (booking.cancellationDetails?.operator === CancelOperator.ByCustomer) return BookingState.Cancelled;
//         if (booking.cancellationDetails?.operator === CancelOperator.ByStaff) return BookingState.Rejected;
//     }
//     if (isConfirmed) return BookingState.Confirmed;

//     return BookingState.Unknown;
// };

export const notificationTriggersLabels: Record<NotificationTriggerType, string> = {
    [NotificationTriggerType.onCreate]: 'Created',
    [NotificationTriggerType.onUpdate]: 'Updated',
    [NotificationTriggerType.onCancel]: 'Cancelled'
};

export const DATE_BOOKING = 'dateBooking';
